'use strict';

const RENDERER_VERSION = 'storybook-renderer-v10.3.0';
const FONT_ASSET_VERSION = 'fontsource-5.3.0-noto-color-emoji-5.3.1';
const STORY_TEXT_LINE_HEIGHT = 1.35;
const STORY_TEXT_PADDING = 10;
const STORY_BUBBLE_PADDING = 15;

const PAGE_SIZES = Object.freeze({
  '5x8': { width: 500, height: 800 },
  '5.06x7.81': { width: 506, height: 781 },
  '5.25x8': { width: 525, height: 800 },
  '5.5x8.5': { width: 550, height: 850 },
  '6x9': { width: 600, height: 900 },
  '6.14x9.21': { width: 614, height: 921 },
  '6.69x9.61': { width: 669, height: 961 },
  '7x10': { width: 700, height: 1000 },
  '7.44x9.69': { width: 744, height: 969 },
  '7.5x9.25': { width: 750, height: 925 },
  '8x10': { width: 800, height: 1000 },
  '8.25x6': { width: 825, height: 600 },
  '8.25x8.25': { width: 825, height: 825 },
  '8.5x8.5': { width: 850, height: 850 },
  '8.5x11': { width: 850, height: 1100 },
  '8.27x11.69': { width: 827, height: 1169 },
  '5.5x8.5-hc': { width: 550, height: 850 },
  '6x9-hc': { width: 600, height: 900 },
  '6.14x9.21-hc': { width: 614, height: 921 },
  '7x10-hc': { width: 700, height: 1000 },
  '8.25x11-hc': { width: 825, height: 1100 },
  '8x5': { width: 800, height: 500 },
  '8.5x5.5': { width: 850, height: 550 },
  '9x6': { width: 900, height: 600 }
});

const FONT_FAMILY_MANIFEST = Object.freeze({
  'Roboto': { package: '@fontsource/roboto', weights: [300, 400, 700] },
  'Open Sans': { package: '@fontsource/open-sans', weights: [300, 400, 700] },
  'Lato': { package: '@fontsource/lato', weights: [300, 400, 700] },
  'Montserrat': { package: '@fontsource/montserrat', weights: [300, 400, 700] },
  'Oswald': { package: '@fontsource/oswald', weights: [300, 400, 700] },
  'Raleway': { package: '@fontsource/raleway', weights: [300, 400, 700] },
  'Ubuntu': { package: '@fontsource/ubuntu', weights: [300, 400, 700] },
  'Merriweather': { package: '@fontsource/merriweather', weights: [300, 400, 700] },
  'Playfair Display': { package: '@fontsource/playfair-display', weights: [400, 700] },
  'Pacifico': { package: '@fontsource/pacifico', weights: [400] },
  'Dancing Script': { package: '@fontsource/dancing-script', weights: [400, 700] },
  'Indie Flower': { package: '@fontsource/indie-flower', weights: [400] },
  'Permanent Marker': { package: '@fontsource/permanent-marker', weights: [400] },
  'Caveat': { package: '@fontsource/caveat', weights: [400, 700] },
  'Fredoka One': {
    package: '@fontsource/fredoka-one',
    weights: [400],
    cssFiles: { 400: 'latin-400.css' }
  },
  'Bangers': { package: '@fontsource/bangers', weights: [400] },
  'Righteous': { package: '@fontsource/righteous', weights: [400] },
  'Audiowide': { package: '@fontsource/audiowide', weights: [400] },
  'Press Start 2P': { package: '@fontsource/press-start-2p', weights: [400] },
  'Bebas Neue': { package: '@fontsource/bebas-neue', weights: [400] },
  'Shadows Into Light': { package: '@fontsource/shadows-into-light', weights: [400] },
  'Patrick Hand': { package: '@fontsource/patrick-hand', weights: [400] },
  'Architects Daughter': { package: '@fontsource/architects-daughter', weights: [400] },
  'Kalam': { package: '@fontsource/kalam', weights: [300, 400, 700] },
  'Noto Sans': { package: '@fontsource/noto-sans', weights: [400, 700], fallback: true },
  'Noto Sans Arabic': { package: '@fontsource/noto-sans-arabic', weights: [400, 700], fallback: true },
  'Noto Sans Devanagari': { package: '@fontsource/noto-sans-devanagari', weights: [400, 700], fallback: true },
  'Noto Sans Hebrew': { package: '@fontsource/noto-sans-hebrew', weights: [400, 700], fallback: true },
  'Noto Sans JP': { package: '@fontsource/noto-sans-jp', weights: [400, 700], fallback: true },
  'Noto Sans KR': { package: '@fontsource/noto-sans-kr', weights: [400, 700], fallback: true },
  'Noto Sans SC': { package: '@fontsource/noto-sans-sc', weights: [400, 700], fallback: true },
  'Noto Sans Thai': { package: '@fontsource/noto-sans-thai', weights: [400, 700], fallback: true },
  'Noto Color Emoji': { package: '@fontsource/noto-color-emoji', weights: [400], fallback: true }
});

const FONT_SUBSTITUTIONS = Object.freeze({
  'Comic Sans MS': 'Patrick Hand', Arial: 'Roboto', Helvetica: 'Open Sans',
  Verdana: 'Lato', Tahoma: 'Ubuntu', 'Trebuchet MS': 'Montserrat',
  'Arial Black': 'Oswald', Impact: 'Bebas Neue', Georgia: 'Merriweather',
  'Times New Roman': 'Playfair Display', Palatino: 'Merriweather',
  'Courier New': 'Roboto', Consolas: 'Ubuntu', Monaco: 'Ubuntu',
  Papyrus: 'Caveat', 'Brush Script MT': 'Dancing Script'
});

const CANONICAL_RENDERER_CSS = `
*{box-sizing:border-box}
html,body{margin:0;padding:0}
.canonical-page{position:relative;display:block;overflow:hidden;margin:0;padding:0;box-sizing:border-box;contain:size layout paint}
.canonical-content{position:absolute;overflow:visible}
.canonical-image{position:absolute;display:block;box-sizing:border-box;transform-origin:center center;overflow:hidden}
.canonical-image img,.canonical-image svg{display:block;width:100%;height:100%}
.canonical-panel{position:absolute;display:block;box-sizing:border-box;pointer-events:none}
.canonical-text{position:absolute;display:block;box-sizing:border-box;transform-origin:center center;overflow:visible}
.canonical-text-decoration{position:absolute;inset:0;width:100%;height:100%;pointer-events:none;overflow:visible}
.canonical-text-inner{position:relative;width:100%;box-sizing:border-box;display:flex;min-width:0;overflow:hidden}
.canonical-text-content{display:block;width:100%;min-width:0;white-space:pre-wrap;overflow-wrap:break-word;word-wrap:break-word;word-break:break-word;unicode-bidi:plaintext}
`;

const finite = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
const clamp = (value, min, max) => Math.min(max, Math.max(min, finite(value, min)));
const px = value => `${Number(finite(value).toFixed(4))}px`;

const escapeHtml = value => String(value == null ? '' : value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const escapeAttribute = escapeHtml;
const safeToken = value => String(value == null ? '' : value).replace(/[^a-zA-Z0-9_-]/g, '-');

// Storybook style fields are persisted user input. Keep every value that is
// interpolated into an inline declaration to a small, non-executable grammar;
// HTML escaping alone does not stop `;background:url(...)` CSS injection.
const sanitizeCssColor = (value, fallback = 'transparent') => {
  const candidate = String(value == null ? '' : value).trim();
  if (candidate.length === 0 || candidate.length > 128) return fallback;
  if (/^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(candidate)) {
    return candidate;
  }
  if (/^[a-z]{1,32}$/i.test(candidate)) return candidate.toLowerCase();
  if (/^rgba?\(\s*[-+.\d%]+(?:[\s,/]+[-+.\d%]+){2,3}\s*\)$/i.test(candidate)) {
    return candidate;
  }
  return fallback;
};

const cssEnum = (value, allowed, fallback) => {
  const candidate = String(value == null ? '' : value).trim().toLowerCase();
  return allowed.includes(candidate) ? candidate : fallback;
};

const cssBorderStyle = value => cssEnum(
  value,
  ['none', 'solid', 'dashed', 'dotted', 'double'],
  'solid'
);

const cssFontWeight = value => {
  const candidate = String(value == null ? '' : value).trim().toLowerCase();
  return /^(?:normal|bold|[1-9]00)$/.test(candidate) ? candidate : 'normal';
};

const styleToString = style => Object.entries(style)
  .filter(([, value]) => value !== undefined && value !== null && value !== '')
  .map(([key, value]) => `${key.replace(/[A-Z]/g, match => `-${match.toLowerCase()}`)}:${String(value)}`)
  .join(';');

const getPageSize = canvasSize => {
  if (canvasSize && typeof canvasSize === 'object') {
    const width = finite(canvasSize.width, 600);
    const height = finite(canvasSize.height, 900);
    return {
      width,
      height,
      widthIn: finite(canvasSize.widthIn, width / 100),
      heightIn: finite(canvasSize.heightIn, height / 100)
    };
  }
  const key = String(canvasSize || '6x9');
  const dimensions = PAGE_SIZES[key] || PAGE_SIZES['6x9'];
  const base = key.replace(/-hc$/, '').split('x').map(Number);
  const widthIn = base.length === 2 && base[0] > 0 ? base[0] : dimensions.width / 100;
  const heightIn = base.length === 2 && base[1] > 0 ? base[1] : dimensions.height / 100;
  return { ...dimensions, widthIn, heightIn };
};

const stripFontQuotes = value => String(value || '').trim().replace(/^['"]|['"]$/g, '');
const resolveProductionFontFamily = value => {
  const primary = stripFontQuotes(String(value || 'Roboto').split(',')[0]);
  return FONT_SUBSTITUTIONS[primary] || primary || 'Roboto';
};

const segmentGraphemes = value => {
  const content = String(value || '');
  if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
    return Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(content), entry => entry.segment);
  }
  return Array.from(content);
};

const fallbackFamilyForGrapheme = (grapheme, hanFamily) => {
  if (/\p{Extended_Pictographic}|\p{Regional_Indicator}|\u20E3/u.test(grapheme)) return 'Noto Color Emoji';
  if (/\p{Script_Extensions=Arabic}/u.test(grapheme)) return 'Noto Sans Arabic';
  if (/\p{Script_Extensions=Devanagari}/u.test(grapheme)) return 'Noto Sans Devanagari';
  if (/\p{Script_Extensions=Hebrew}/u.test(grapheme)) return 'Noto Sans Hebrew';
  if (/\p{Script_Extensions=Thai}/u.test(grapheme)) return 'Noto Sans Thai';
  if (/\p{Script_Extensions=Hiragana}|\p{Script_Extensions=Katakana}/u.test(grapheme)) return 'Noto Sans JP';
  if (/\p{Script_Extensions=Hangul}/u.test(grapheme)) return 'Noto Sans KR';
  if (/\p{Script_Extensions=Han}/u.test(grapheme)) return hanFamily;
  if (/\p{Script_Extensions=Cyrillic}|\p{Script_Extensions=Greek}/u.test(grapheme)) return 'Noto Sans';
  return undefined;
};

const resolveProductionFontRuns = (fontFamily, content) => {
  const family = resolveProductionFontFamily(fontFamily);
  const value = String(content || '');
  const firstJapanese = value.search(/\p{Script_Extensions=Hiragana}|\p{Script_Extensions=Katakana}/u);
  const firstKorean = value.search(/\p{Script_Extensions=Hangul}/u);
  const hanFamily = firstJapanese >= 0 && (firstKorean < 0 || firstJapanese < firstKorean)
    ? 'Noto Sans JP'
    : firstKorean >= 0 ? 'Noto Sans KR' : 'Noto Sans SC';
  const samples = new Map([[family, '']]);
  for (const grapheme of segmentGraphemes(value)) {
    const fallback = fallbackFamilyForGrapheme(grapheme, hanFamily);
    const target = fallback || family;
    if (!samples.has(target)) samples.set(target, '');
    samples.set(target, samples.get(target) + grapheme);
  }
  if (!samples.get(family)) samples.set(family, value || 'Tiny Little Fox');
  return Array.from(samples, ([runFamily, sample]) => ({
    family: runFamily,
    sample: sample || value || 'Tiny Little Fox',
    fallback: runFamily !== family
  }));
};

const quoteCssFontFamily = family => `"${String(family).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
const toCssFontFamily = (fontFamily, content = '') => resolveProductionFontRuns(fontFamily, content)
  .map(run => quoteCssFontFamily(run.family)).join(', ');

const resolveTextDirection = content => {
  for (const grapheme of segmentGraphemes(content)) {
    if (/\p{Script_Extensions=Arabic}|\p{Script_Extensions=Hebrew}/u.test(grapheme)) return 'rtl';
    if (/\p{Letter}|\p{Number}/u.test(grapheme)) return 'ltr';
  }
  return 'ltr';
};

const normalizeOpacity = value => {
  const numeric = finite(value, 1);
  return clamp(numeric > 1 ? numeric / 100 : numeric, 0, 1);
};

const colorWithOpacity = (color, opacity) => {
  const value = sanitizeCssColor(color, 'transparent');
  const alpha = normalizeOpacity(opacity);
  if (value === 'transparent' || alpha === 0) return 'transparent';
  const shortHex = value.match(/^#([0-9a-f]{3})$/i);
  const fullHex = value.match(/^#([0-9a-f]{6})$/i);
  const hex = fullHex ? fullHex[1] : shortHex ? shortHex[1].split('').map(char => char + char).join('') : null;
  if (hex) {
    return `rgba(${parseInt(hex.slice(0, 2), 16)}, ${parseInt(hex.slice(2, 4), 16)}, ${parseInt(hex.slice(4, 6), 16)}, ${alpha})`;
  }
  return alpha === 1 ? value : `color-mix(in srgb, ${value} ${alpha * 100}%, transparent)`;
};

const isBubbleStyle = styleId => String(styleId || '').includes('bubble');
const isTearStyle = styleId => String(styleId || '') === 'tear-box';

const nearestWeight = (family, requestedWeight) => {
  const manifest = FONT_FAMILY_MANIFEST[family];
  if (!manifest) return 400;
  const requested = requestedWeight === 'bold' ? 700 : requestedWeight === 'normal' ? 400 : finite(requestedWeight, 400);
  return manifest.weights.reduce((best, candidate) => (
    Math.abs(candidate - requested) < Math.abs(best - requested) ? candidate : best
  ), manifest.weights[0]);
};

const getImageRenderModel = (image, options = {}) => {
  const scale = finite(options.scale, 1) || 1;
  const canvas = getPageSize(options.canvasSize || options.pageSize);
  const bleedPx = Math.max(0, finite(options.bleedPx, 0));
  const useBleed = options.bleed === true || bleedPx > 0;
  const rotation = finite(image.rotation, 0);
  const legacyFit = String(image.url || '').includes('assets/') ? 'contain' : 'cover';
  const fit = image.bleed === 'full'
    ? 'cover'
    : cssEnum(image.fit, ['contain', 'cover'], legacyFit);
  const renderAsFullBleed = image.bleed === 'full';
  const baseX = finite(image.x);
  const baseY = finite(image.y);
  const baseWidth = Math.max(0, finite(image.width));
  const baseHeight = Math.max(0, finite(image.height));
  const extendEdges = useBleed && fit === 'cover' && rotation === 0;
  const extendLeft = extendEdges && baseX <= 0.01;
  const extendTop = extendEdges && baseY <= 0.01;
  const extendRight = extendEdges && baseX + baseWidth >= canvas.width - 0.01;
  const extendBottom = extendEdges && baseY + baseHeight >= canvas.height - 0.01;
  const x = renderAsFullBleed ? -bleedPx : baseX * scale - (extendLeft ? bleedPx : 0);
  const y = renderAsFullBleed ? -bleedPx : baseY * scale - (extendTop ? bleedPx : 0);
  const width = renderAsFullBleed
    ? canvas.width * scale + bleedPx * 2
    : baseWidth * scale + (extendLeft ? bleedPx : 0) + (extendRight ? bleedPx : 0);
  const height = renderAsFullBleed
    ? canvas.height * scale + bleedPx * 2
    : baseHeight * scale + (extendTop ? bleedPx : 0) + (extendBottom ? bleedPx : 0);
  const shape = image.shape === 'round' || image.shape === 'ellipse' ? image.shape : 'rect';
  const borderWidth = Math.max(0, finite(image.borderWidth, 0) * scale);
  const borderStyle = cssBorderStyle(image.borderStyle);
  const borderColor = sanitizeCssColor(image.borderColor, '#000000');
  const flipX = image.flipHorizontal ? -1 : 1;
  const flipY = image.flipVertical ? -1 : 1;
  const outerStyle = {
    position: 'absolute', left: px(x), top: px(y), width: px(width), height: px(height),
    transform: `rotate(${rotation}deg)`, transformOrigin: 'center center',
    overflow: 'hidden', boxSizing: 'border-box',
    border: borderWidth > 0 && borderStyle !== 'none'
      ? `${px(borderWidth)} ${borderStyle} ${borderColor}` : 'none',
    borderRadius: shape === 'round' ? '50%' : shape === 'ellipse' ? '50%' : '0'
  };
  const mediaStyle = {
    width: '100%', height: '100%', display: 'block', objectFit: fit,
    transform: `scaleX(${flipX}) scaleY(${flipY})`, transformOrigin: 'center center'
  };
  return {
    id: String(image.id || ''), url: String(image.url || ''), x, y, width, height,
    rotation, fit, shape, feather: renderAsFullBleed ? 0 : clamp(image.feather || 0, 0, 100),
    flipX, flipY, borderWidth, borderStyle, borderColor,
    outerStyle, mediaStyle, renderAsFullBleed
  };
};

const PANEL_TREATMENTS = Object.freeze([
  'integrated', 'paper-fade', 'solid-paper', 'framed-vignette'
]);

const getPanelRenderModel = (panel, options = {}) => {
  const scale = finite(options.scale, 1) || 1;
  const treatment = PANEL_TREATMENTS.includes(panel.treatment)
    ? panel.treatment
    : 'solid-paper';
  const x = finite(panel.x) * scale;
  const y = finite(panel.y) * scale;
  const width = Math.max(0, finite(panel.width) * scale);
  const height = Math.max(0, finite(panel.height) * scale);
  const opacity = normalizeOpacity(panel.opacity == null ? 1 : panel.opacity);
  const backgroundColor = sanitizeCssColor(panel.backgroundColor, '#fffdf5');
  const opaqueColor = colorWithOpacity(backgroundColor, opacity);
  const fadeEdge = ['top', 'bottom', 'left', 'right'].includes(panel.fadeEdge)
    ? panel.fadeEdge
    : 'none';
  const maximumFade = fadeEdge === 'left' || fadeEdge === 'right' ? width : height;
  const defaultFadeSize = (fadeEdge === 'left' || fadeEdge === 'right'
    ? finite(panel.width) : finite(panel.height)) * 0.2;
  const fadeSize = clamp(finite(panel.fadeSize, defaultFadeSize) * scale, 0, maximumFade);
  let background = treatment === 'integrated' ? 'transparent' : opaqueColor;
  if (treatment === 'paper-fade' && fadeEdge !== 'none' && fadeSize > 0) {
    if (fadeEdge === 'top') {
      background = `linear-gradient(to bottom,transparent 0px,${opaqueColor} ${px(fadeSize)},${opaqueColor} 100%)`;
    } else if (fadeEdge === 'bottom') {
      background = `linear-gradient(to bottom,${opaqueColor} 0px,${opaqueColor} calc(100% - ${px(fadeSize)}),transparent 100%)`;
    } else if (fadeEdge === 'left') {
      background = `linear-gradient(to right,transparent 0px,${opaqueColor} ${px(fadeSize)},${opaqueColor} 100%)`;
    } else {
      background = `linear-gradient(to right,${opaqueColor} 0px,${opaqueColor} calc(100% - ${px(fadeSize)}),transparent 100%)`;
    }
  }
  const borderWidth = Math.max(0, finite(panel.borderWidth, treatment === 'framed-vignette' ? 1 : 0) * scale);
  const borderStyle = cssBorderStyle(panel.borderStyle);
  const borderColor = sanitizeCssColor(panel.borderColor, '#a9d6e4');
  const cornerRadius = Math.max(0, finite(panel.cornerRadius, 0) * scale);
  const outerStyle = {
    position: 'absolute', left: px(x), top: px(y), width: px(width), height: px(height),
    boxSizing: 'border-box', pointerEvents: 'none', background,
    border: borderWidth > 0 && borderStyle !== 'none'
      ? `${px(borderWidth)} ${borderStyle} ${borderColor}` : 'none',
    borderRadius: px(cornerRadius), overflow: 'hidden'
  };
  return {
    id: String(panel.id || ''), x, y, width, height, treatment, opacity,
    backgroundColor, fadeEdge, fadeSize, borderWidth, borderStyle, borderColor,
    cornerRadius, outerStyle
  };
};

const deterministicNoise = seed => {
  let value = 2166136261;
  for (const character of String(seed)) {
    value ^= character.charCodeAt(0);
    value = Math.imul(value, 16777619);
  }
  return () => {
    value += 0x6D2B79F5;
    let next = value;
    next = Math.imul(next ^ (next >>> 15), next | 1);
    next ^= next + Math.imul(next ^ (next >>> 7), next | 61);
    return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
  };
};

const tearPath = (width, height, intensity, seed) => {
  const random = deterministicNoise(seed);
  const step = Math.max(6, 14 - intensity * 0.08);
  const depth = 2 + intensity * 0.055;
  const points = [];
  for (let x = 0; x < width; x += step) points.push([x, (random() - 0.5) * depth + depth]);
  points.push([width, depth]);
  for (let y = step; y < height; y += step) points.push([width - depth + (random() - 0.5) * depth, y]);
  points.push([width - depth, height]);
  for (let x = width - step; x > 0; x -= step) points.push([x, height - depth + (random() - 0.5) * depth]);
  points.push([depth, height]);
  for (let y = height - step; y > 0; y -= step) points.push([depth + (random() - 0.5) * depth, y]);
  return `M ${points.map(point => `${Number(point[0].toFixed(2))} ${Number(point[1].toFixed(2))}`).join(' L ')} Z`;
};

const bubblePath = (styleId, width, height) => {
  const right = String(styleId).includes('-right');
  if (String(styleId).includes('shout')) {
    const commands = ['M 5 10'];
    const spikeWidth = (width - 10) / 10;
    for (let index = 0; index < 10; index += 1) {
      commands.push(`L ${5 + spikeWidth * index + spikeWidth / 2} ${index % 2 === 0 ? 0 : 10}`);
      commands.push(`L ${5 + spikeWidth * (index + 1)} 10`);
    }
    commands.push(`L ${width - 5} 10 L ${width} ${height / 2} L ${width - 5} ${height - 10}`);
    commands.push(right
      ? `L ${width * 0.92} ${height - 5} L ${width * 0.9} ${height + 30} L ${width * 0.85} ${height - 10}`
      : `L ${width * 0.15} ${height - 10} L ${width * 0.1} ${height + 30} L ${width * 0.08} ${height - 5}`);
    commands.push(`L 10 ${height - 5} L 5 ${height - 10} L 0 ${height / 2} Z`);
    return commands.join(' ');
  }
  if (String(styleId).includes('whisper')) {
    const tail = right
      ? `L ${width * 0.88} ${height - 3} Q ${width * 0.87} ${height + 8} ${width * 0.88} ${height + 15} Q ${width * 0.85} ${height + 8} ${width * 0.84} ${height - 3}`
      : `L ${width * 0.16} ${height - 3} Q ${width * 0.15} ${height + 8} ${width * 0.12} ${height + 15} Q ${width * 0.13} ${height + 8} ${width * 0.12} ${height - 3}`;
    return `M 15 5 Q ${width - 5} 5 ${width - 5} 15 L ${width - 5} ${height - 15} Q ${width - 5} ${height - 5} ${width - 15} ${height - 5} ${tail} L 15 ${height - 5} Q 5 ${height - 5} 5 ${height - 15} L 5 15 Q 5 5 15 5 Z`;
  }
  if (String(styleId).includes('rectangular')) {
    const tail = right
      ? `L ${width * 0.88} ${height - 5} L ${width * 0.86} ${height + 20} L ${width * 0.82} ${height - 5}`
      : `L ${width * 0.18} ${height - 5} L ${width * 0.14} ${height + 20} L ${width * 0.12} ${height - 5}`;
    return `M 5 5 L ${width - 5} 5 L ${width - 5} ${height - 5} ${tail} L 5 ${height - 5} Z`;
  }
  const tailCenter = right ? width * 0.65 : width * 0.35;
  return `M 20 5 H ${width - 20} Q ${width - 5} 5 ${width - 5} 20 V ${height - 20} Q ${width - 5} ${height - 5} ${width - 20} ${height - 5} H ${tailCenter + 12} Q ${tailCenter + 6} ${height + 8} ${tailCenter} ${height + 28} Q ${tailCenter - 6} ${height + 8} ${tailCenter - 12} ${height - 5} H 20 Q 5 ${height - 5} 5 ${height - 20} V 20 Q 5 5 20 5 Z`;
};

const thoughtBubblePath = (width, height) => {
  const centerX = width / 2;
  const centerY = height / 2;
  const radiusX = width * 0.42;
  const radiusY = height * 0.35;
  const commands = [];
  for (let index = 0; index < 12; index += 1) {
    const angle = index / 12 * Math.PI * 2;
    const nextAngle = (index + 1) / 12 * Math.PI * 2;
    const x = centerX + Math.cos(angle) * radiusX;
    const y = centerY + Math.sin(angle) * radiusY;
    const nextX = centerX + Math.cos(nextAngle) * radiusX;
    const nextY = centerY + Math.sin(nextAngle) * radiusY;
    const midAngle = (angle + nextAngle) / 2;
    const controlX = centerX + Math.cos(midAngle) * radiusX * 1.2;
    const controlY = centerY + Math.sin(midAngle) * radiusY * 1.2;
    if (index === 0) commands.push(`M ${x} ${y}`);
    commands.push(`Q ${controlX} ${controlY} ${nextX} ${nextY}`);
  }
  return `${commands.join(' ')} Z`;
};

const getTextRenderModel = (text, options = {}) => {
  const scale = finite(options.scale, 1) || 1;
  const styleId = String(text.styleId || '');
  const bubble = isBubbleStyle(styleId);
  const tear = isTearStyle(styleId);
  const defaultPadding = bubble ? STORY_BUBBLE_PADDING : STORY_TEXT_PADDING;
  const padding = Math.max(0, finite(text.padding, defaultPadding)) * scale;
  const width = Math.max(0, finite(text.width) * scale);
  const textHeight = Math.max(0, finite(text.height) * scale);
  const decorationHeight = bubble ? textHeight + 30 * scale : textHeight;
  const backgroundColor = sanitizeCssColor(
    text.backgroundColor,
    (bubble || tear) ? '#ffffff' : 'transparent'
  );
  const backgroundOpacity = normalizeOpacity(text.backgroundOpacity == null ? 1 : text.backgroundOpacity);
  const borderWidth = Math.max(0, finite(text.borderWidth, 0) * scale);
  const borderStyle = cssBorderStyle(text.borderStyle);
  const borderColor = sanitizeCssColor(text.borderColor, '#000000');
  const verticalAlign = text.verticalAlign || (bubble ? 'center' : 'top');
  const alignItems = verticalAlign === 'bottom' ? 'flex-end' : verticalAlign === 'center' ? 'center' : 'flex-start';
  const textAlign = ['left', 'right', 'center'].includes(text.textAlign) ? text.textAlign : 'center';
  const fontFamily = resolveProductionFontFamily(text.fontFamily || 'Roboto');
  const fontWeight = cssFontWeight(text.fontWeight);
  const fontStyle = cssEnum(text.fontStyle, ['normal', 'italic'], 'normal');
  const lineHeight = clamp(finite(text.lineHeight, STORY_TEXT_LINE_HEIGHT), 0.8, 3);
  const direction = resolveTextDirection(text.content || '');
  let textShadow = 'none';
  if (styleId === '3d-title') {
    const first = sanitizeCssColor(text.title3dColor1, '#4af7ff');
    const second = sanitizeCssColor(text.title3dColor2, '#165bfb');
    textShadow = `3px 1px 1px ${first},2px 2px 1px ${second},4px 2px 1px ${first},3px 3px 1px ${second},5px 3px 1px ${first},4px 4px 1px ${second},6px 4px 1px ${first},5px 5px 1px ${second},7px 5px 1px ${first},6px 6px 1px ${second},8px 6px 1px ${first},7px 7px 1px ${second},9px 7px 1px ${first}`;
  } else if (text.textShadow) {
    textShadow = `${finite(text.shadowOffsetX)}px ${finite(text.shadowOffsetY)}px ${Math.max(0, finite(text.shadowBlur))}px ${sanitizeCssColor(text.shadowColor, '#000000')}`;
  }
  const outerStyle = {
    position: 'absolute', left: px(finite(text.x) * scale), top: px(finite(text.y) * scale),
    width: px(width), height: px(decorationHeight), transform: `rotate(${finite(text.rotation)}deg)`,
    transformOrigin: 'center center', overflow: 'visible', boxSizing: 'border-box'
  };
  const innerStyle = {
    position: 'relative', width: '100%', height: px(textHeight), display: 'flex',
    alignItems, padding: px(padding), boxSizing: 'border-box', overflow: 'hidden',
    fontFamily: toCssFontFamily(fontFamily, text.content || ''), fontSize: px(Math.max(1, finite(text.fontSize, 16) * scale)),
    lineHeight: String(lineHeight), fontWeight, fontStyle,
    textDecoration: cssEnum(
      text.textDecoration,
      ['none', 'underline', 'line-through', 'underline line-through'],
      'none'
    ),
    color: sanitizeCssColor(text.color, '#000000'), textAlign,
    direction, unicodeBidi: 'plaintext', whiteSpace: 'pre-wrap', overflowWrap: 'break-word',
    wordWrap: 'break-word', wordBreak: 'break-word', textShadow,
    backgroundColor: (bubble || tear) ? 'transparent' : colorWithOpacity(backgroundColor, backgroundOpacity),
    border: (bubble || tear || borderWidth <= 0 || borderStyle === 'none')
      ? 'none' : `${px(borderWidth)} ${borderStyle} ${borderColor}`
  };
  return {
    id: String(text.id || ''), content: String(text.content || ''), styleId, bubble, tear,
    x: finite(text.x) * scale, y: finite(text.y) * scale, width, textHeight, decorationHeight,
    padding, fontFamily, fontWeight, fontStyle, fontSize: Math.max(1, finite(text.fontSize, 16) * scale),
    backgroundColor, backgroundOpacity, borderWidth, borderStyle, borderColor,
    verticalAlign, textAlign, direction, outerStyle, innerStyle
  };
};

const getBubbleSvgMarkup = (text, options = {}) => {
  const model = getTextRenderModel(text, options);
  if (!model.bubble && !model.tear) return '';
  const width = model.width;
  const height = model.textHeight;
  const svgHeight = model.decorationHeight;
  const fill = escapeAttribute(model.backgroundColor);
  const fillOpacity = model.backgroundOpacity;
  const stroke = model.borderWidth > 0 && model.borderStyle !== 'none' ? escapeAttribute(model.borderColor) : 'none';
  const dash = model.borderStyle === 'dashed' ? `${6 * (options.scale || 1)} ${5 * (options.scale || 1)}`
    : model.borderStyle === 'dotted' ? `${2 * (options.scale || 1)} ${4 * (options.scale || 1)}` : '';
  const strokeAttrs = `stroke="${stroke}" stroke-width="${model.borderWidth}"${dash ? ` stroke-dasharray="${dash}"` : ''}`;
  if (model.tear) {
    const intensity = clamp(text.tearBoxIntensity == null ? 50 : text.tearBoxIntensity, 0, 100);
    const path = tearPath(width, height, intensity, `${model.id}|${intensity}|${RENDERER_VERSION}`);
    return `<svg class="canonical-text-decoration" aria-hidden="true" viewBox="0 0 ${width} ${svgHeight}" preserveAspectRatio="none"><path d="${path}" fill="${fill}" fill-opacity="${fillOpacity}" ${strokeAttrs}/></svg>`;
  }
  if (model.styleId.includes('thought')) {
    const circles = [
      [model.styleId.includes('-right') ? width * 0.76 : width * 0.24, height * 0.9, 7],
      [model.styleId.includes('-right') ? width * 0.86 : width * 0.14, height + 7, 4.5],
      [model.styleId.includes('-right') ? width * 0.94 : width * 0.06, height + 19, 2.5]
    ].map(([cx, cy, radius]) => `<circle cx="${cx}" cy="${cy}" r="${radius}" fill="${fill}" fill-opacity="${fillOpacity}" ${strokeAttrs}/>`).join('');
    return `<svg class="canonical-text-decoration" aria-hidden="true" viewBox="0 0 ${width} ${svgHeight}" preserveAspectRatio="none"><path d="${thoughtBubblePath(width, height)}" fill="${fill}" fill-opacity="${fillOpacity}" ${strokeAttrs}/>${circles}</svg>`;
  }
  const path = bubblePath(model.styleId, width, height);
  return `<svg class="canonical-text-decoration" aria-hidden="true" viewBox="0 0 ${width} ${svgHeight}" preserveAspectRatio="none"><path d="${path}" fill="${fill}" fill-opacity="${fillOpacity}" ${strokeAttrs}/></svg>`;
};

const renderImageHtml = (image, options, page) => {
  const model = getImageRenderModel(image, options);
  const resolved = typeof options.resolveImageUrl === 'function'
    ? options.resolveImageUrl(model.url, image, page)
    : model.url;
  const src = escapeAttribute(resolved == null ? '' : resolved);
  const explicitAltText = String(image.altText || '').replace(/\s+/g, ' ').trim();
  const characterName = String(image.characterName || '').trim();
  const prompt = String(image.prompt || '').replace(/\s+/g, ' ').trim();
  const accessibleName = explicitAltText
    || (characterName ? `Character illustration: ${characterName}` : '')
    || (prompt ? `Storybook illustration: ${prompt.slice(0, 120)}` : '')
    || (image.layoutRole === 'background' ? 'Background illustration' : '')
    || (image.layoutRole === 'decoration' ? 'Decorative illustration' : '')
    || 'Storybook illustration';
  const attrs = `class="canonical-image image-element" data-element-id="${escapeAttribute(model.id)}" role="img" aria-label="${escapeAttribute(accessibleName)}" style="${escapeAttribute(styleToString(model.outerStyle))}"`;
  if (model.feather > 0) {
    const maskId = `canonical-feather-${safeToken(model.id)}-${safeToken(RENDERER_VERSION)}`;
    const blur = Math.min(80 * (options.scale || 1), model.feather / 100 * Math.max(model.width, model.height) * 0.08);
    const clipId = `${maskId}-shape`;
    const clip = model.shape === 'rect' ? '' : `<clipPath id="${clipId}"><ellipse cx="${model.width / 2}" cy="${model.height / 2}" rx="${model.width / 2}" ry="${model.height / 2}"/></clipPath>`;
    const preserve = model.fit === 'cover' ? 'xMidYMid slice' : 'xMidYMid meet';
    const flip = `translate(${model.flipX < 0 ? model.width : 0} ${model.flipY < 0 ? model.height : 0}) scale(${model.flipX} ${model.flipY})`;
    return `<div ${attrs}><img src="${src}" alt="" aria-hidden="true" style="position:absolute;width:1px;height:1px;opacity:0;pointer-events:none"/><svg aria-hidden="true" viewBox="0 0 ${model.width} ${model.height}" preserveAspectRatio="none"><defs><filter id="${maskId}-blur" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur in="SourceGraphic" stdDeviation="${blur}"/></filter><mask id="${maskId}"><rect x="0" y="0" width="${model.width}" height="${model.height}" fill="white" filter="url(#${maskId}-blur)"/></mask>${clip}</defs><g mask="url(#${maskId})"${clip ? ` clip-path="url(#${clipId})"` : ''}><image href="${src}" x="0" y="0" width="${model.width}" height="${model.height}" preserveAspectRatio="${preserve}" transform="${flip}"/></g></svg></div>`;
  }
  return `<div ${attrs}><img src="${src}" alt="" aria-hidden="true" style="${escapeAttribute(styleToString(model.mediaStyle))}"/></div>`;
};

const renderPanelHtml = (panel, options) => {
  const model = getPanelRenderModel(panel, options);
  return `<div class="canonical-panel panel-element" data-element-id="${escapeAttribute(model.id)}" data-treatment="${escapeAttribute(model.treatment)}" aria-hidden="true" style="${escapeAttribute(styleToString(model.outerStyle))}"></div>`;
};

const renderTextHtml = (text, options) => {
  const model = getTextRenderModel(text, options);
  const decoration = getBubbleSvgMarkup(text, options);
  return `<div class="canonical-text text-element" data-element-id="${escapeAttribute(model.id)}" style="${escapeAttribute(styleToString(model.outerStyle))}">${decoration}<div class="canonical-text-inner" style="${escapeAttribute(styleToString(model.innerStyle))}"><div class="canonical-text-content text-content-node">${escapeHtml(model.content)}</div></div></div>`;
};

const renderPageHtml = (page, options = {}) => {
  const pageSize = getPageSize(options.canvasSize || options.pageSize);
  const scale = finite(options.scale, 1) || 1;
  const bleedPx = Math.max(0, finite(options.bleedPx, 0));
  const pageWidth = pageSize.width * scale + bleedPx * 2;
  const pageHeight = pageSize.height * scale + bleedPx * 2;
  const pageIndex = finite(options.pageIndex, 0);
  const className = ['canonical-page', options.className].filter(Boolean).join(' ');
  const ariaLabel = typeof options.ariaLabel === 'function'
    ? options.ariaLabel(page, pageIndex)
    : options.ariaLabel;
  const pageAttrs = [
    `class="${escapeAttribute(className)}"`,
    `data-page-index="${pageIndex}"`,
    page.id != null ? `data-page-id="${escapeAttribute(page.id)}"` : '',
    ariaLabel ? `aria-label="${escapeAttribute(ariaLabel)}"` : ''
  ].filter(Boolean).join(' ');
  const pageStyle = styleToString({
    position: 'relative', width: px(pageWidth), height: px(pageHeight),
    backgroundColor: sanitizeCssColor(page.backgroundColor, '#ffffff'), overflow: 'hidden',
    pageBreakAfter: options.isLastPage ? 'auto' : 'always'
  });
  const contentStyle = styleToString({
    position: 'absolute', left: px(bleedPx), top: px(bleedPx),
    width: px(pageSize.width * scale), height: px(pageSize.height * scale), overflow: 'visible'
  });
  const childOptions = { ...options, scale, bleedPx, pageSize, canvasSize: pageSize };
  const images = (page.imageElements || []).map(image => renderImageHtml(image, childOptions, page)).join('');
  const panels = (page.panelElements || []).map(panel => renderPanelHtml(panel, childOptions)).join('');
  const texts = (page.textElements || []).map(text => renderTextHtml(text, childOptions)).join('');
  return `<div ${pageAttrs} style="${escapeAttribute(pageStyle)}"><div class="canonical-content" style="${escapeAttribute(contentStyle)}">${images}${panels}${texts}</div></div>`;
};

const renderDocumentHtml = options => {
  const pages = Array.isArray(options.pages) ? options.pages : [];
  const pageSize = getPageSize(options.canvasSize);
  const bleedInches = options.bleed === true ? finite(options.bleedInches, 0.125) : 0;
  const bleedPx = bleedInches * 96;
  const scale = Math.min(pageSize.widthIn * 96 / pageSize.width, pageSize.heightIn * 96 / pageSize.height);
  const pageWidthIn = pageSize.widthIn + bleedInches * 2;
  const pageHeightIn = pageSize.heightIn + bleedInches * 2;
  const pageHtml = pages.map((page, index) => renderPageHtml(page, {
    canvasSize: pageSize, scale, bleedPx, bleed: options.bleed === true,
    resolveImageUrl: options.resolveImageUrl, className: options.className,
    ariaLabel: options.ariaLabel || ((_, pageIndex) => `Storybook page ${pageIndex + 1}`),
    pageIndex: index, isLastPage: index === pages.length - 1
  })).join('\n');
  const contentSecurityPolicy = [
    "default-src 'none'",
    "img-src data: blob: file: https://pdf-image-cache.local",
    "font-src data: file:",
    "style-src 'unsafe-inline'",
    "connect-src 'none'",
    "media-src 'none'",
    "object-src 'none'",
    "frame-src 'none'"
  ].join('; ');
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${contentSecurityPolicy}"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${escapeHtml(options.title || 'Storybook')}</title><style>${options.fontCss || ''}\n@page{size:${pageWidthIn}in ${pageHeightIn}in;margin:0;padding:0}\n${CANONICAL_RENDERER_CSS}</style></head><body>${pageHtml}</body></html>`;
};

module.exports = {
  RENDERER_VERSION,
  FONT_ASSET_VERSION,
  STORY_TEXT_LINE_HEIGHT,
  STORY_TEXT_PADDING,
  STORY_BUBBLE_PADDING,
  PAGE_SIZES,
  FONT_FAMILY_MANIFEST,
  CANONICAL_RENDERER_CSS,
  escapeHtml,
  sanitizeCssColor,
  getPageSize,
  resolveProductionFontFamily,
  resolveProductionFontRuns,
  toCssFontFamily,
  resolveTextDirection,
  nearestWeight,
  getImageRenderModel,
  getPanelRenderModel,
  getTextRenderModel,
  getBubbleSvgMarkup,
  renderPanelHtml,
  renderPageHtml,
  renderDocumentHtml
};
